/**
 * Localization Hreflang Manager - Moon Moon Agency
 * Reecrit les href du selecteur pays/langue avec le slug traduit de la locale cible.
 *
 * Probleme : Liquid n'expose pas le handle traduit d'une ressource dans une autre
 * locale. Le selecteur colle donc le prefixe de locale sur le path courant
 * (/it-it/pages/magnesio-bisglicinato -> /de-de/pages/magnesio-bisglicinato),
 * et Shopify rattrape en 301 vers le vrai slug (/de-de/pages/aktives-magnesium).
 * Resultat : ~67% des liens inter-locales sont des redirections (400K URLs).
 *
 * Solution : les <link rel="alternate" hreflang> du <head> (injectes en SSR par
 * l'app de traduction) contiennent DEJA l'URL finale traduite de chaque locale.
 * Chaque option du selecteur porte un data-hreflang qui sert de cle de mapping.
 * On reecrit les data-href depuis cette source -> acces direct en 200, zero redirection.
 *
 * Progressive enhancement : sans JS (ou sans hreflang), les data-href Liquid
 * d'origine restent en place et Shopify redirige comme avant. Rien ne casse.
 *
 * SEO (ClickUp 86caz0bb2) : les options sont des <span role="link" data-href>
 * et non des <a href> — les liens sont obfusques pour Google, le maillage
 * inter-langues passe par les <link rel="alternate" hreflang> du <head>.
 */

/*********************************************************************************************
 * 🔗 LOCALIZATION HREFLANG MANAGER
 */
const LocalizationHreflangManager = {
	config: {
		selectors: {
			alternates: 'link[rel="alternate"][hreflang]',
			links: '.mm-localization-links__link',
		},
	},

	init() {
		const links = document.querySelectorAll(this.config.selectors.links);
		if (!links.length) return; // Guard : selecteur absent du DOM

		const alternates = this._buildAlternatesMap();
		if (!alternates.size) return; // Guard : pas de hreflang -> on garde les href Liquid

		let rewritten = 0;
		links.forEach((link) => {
			if (this._rewriteLink(link, alternates)) rewritten++;
		});

		window.log('info', '🔗 [LOCALIZATION_HREFLANG]', `Initialized (${rewritten}/${links.length} liens reecrits)`);
	},

	/**
	 * Indexe les <link hreflang> du <head> : code de locale -> URL traduite.
	 * Les codes sont normalises en minuscules (hreflang est insensible a la casse :
	 * le <head> expose "de-DE" quand le selecteur ecrit parfois "de-de").
	 */
	_buildAlternatesMap() {
		const map = new Map();

		document.querySelectorAll(this.config.selectors.alternates).forEach((alternate) => {
			const code = alternate.getAttribute('hreflang')?.toLowerCase();
			const href = alternate.getAttribute('href');
			if (code && href) map.set(code, href);
		});

		return map;
	},

	/**
	 * Reecrit un lien depuis la map. Retourne true si le href a change.
	 *
	 * Deux niveaux de correspondance :
	 * 1. Exact (fr-BE, de-DE...) : le cas nominal.
	 * 2. Langue seule (fr-GP -> fr) : les DOM-TOM et pays partageant une web presence
	 *    n'ont pas de hreflang dedie, mais pointent vers la meme URL que leur langue.
	 *    Le selecteur de LANGUE qualifie sa cle avec le pays courant (nl -> nl-BE) :
	 *    sans ca, changer de langue a pays constant ne matchait aucun hreflang et
	 *    retombait sur la 301 (/nl-be/ + slug allemand).
	 * Sans correspondance, on ne touche a rien : le href Liquid (+ 301 Shopify) reste
	 * le comportement de repli, jamais un lien casse.
	 */
	_rewriteLink(link, alternates) {
		const code = link.getAttribute('data-hreflang')?.toLowerCase();
		if (!code) return false;

		const target = alternates.get(code) || alternates.get(code.split('-')[0]);
		if (!target) return false;

		// Conserve les query params reconstruits cote Liquid (?q=, ?page=) que les
		// hreflang, qui pointent vers l'URL canonique nue, ne portent pas.
		const currentQuery = link.getAttribute('data-href')?.split('?')[1];
		const href = currentQuery ? `${target}?${currentQuery}` : target;

		if (link.getAttribute('data-href') === href) return false;

		link.setAttribute('data-href', href);
		return true;
	},
};

// Auto-init avec verification readyState
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => LocalizationHreflangManager.init());
} else {
	LocalizationHreflangManager.init();
}
